import { useState, ComponentPropsWithoutRef, forwardRef, useMemo } from 'react'
import { cn } from '~shared/utils/cn'
import * as flag from 'country-flag-icons/string/3x2'
import { COUNTRIES, type Country } from './const'
import {
  parsePhoneNumberFromString,
  AsYouType,
  CountryCode,
  getExampleNumber
} from 'libphonenumber-js'

import examples from 'libphonenumber-js/mobile/examples'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { isError } from 'ui/fractals/utils'

// const ChevronDownIcon = () => {
//   return (
//     <svg
//       width='12'
//       height='12'
//       viewBox='0 0 15 15'
//       fill='none'
//       xmlns='http://www.w3.org/2000/svg'>
//       <path
//         d='M4.18179 6.18181C4.35753 6.00608 4.64245 6.00608 4.81819 6.18181L7.49999 8.86362L10.1818 6.18181C10.3575 6.00608 10.6424 6.00608 10.8182 6.18181C10.9939 6.35755 10.9939 6.64247 10.8182 6.81821L7.81819 9.81821C7.73379 9.9026 7.61934 9.95001 7.49999 9.95001C7.38064 9.95001 7.26618 9.9026 7.18179 9.81821L4.18179 6.81821C4.00605 6.64247 4.00605 6.35755 4.18179 6.18181Z'
//         fill='currentColor'
//         fillRule='evenodd'
//         clipRule='evenodd'
//       />
//     </svg>
//   )
// }

const Flag = ({ country }: { country: Country }) => {
  const flagElement = (flag as Record<string, string>)[country.code]

  return flagElement ? (
    <div
      className='w-6'
      /* eslint-disable-next-line react/no-danger */
      dangerouslySetInnerHTML={{
        __html: flagElement
      }}
    />
  ) : (
    <span>{country.code}</span>
  )
}

const FakePlaceholder = (props: { example: string; phone: string }) => {
  const [dial = '', ...exampleArr] = props.example.split(' ')
  const placeholder = exampleArr.join(' ').replace(/\d/g, '9')

  const [phoneDial, ...phoneArr] = props.phone.split(' ')
  const phone = phoneArr.join(' ')

  const showDial = phoneDial === '+'
  const showPlaceholder = showDial || (phoneDial === dial && !phone)

  return (
    <>
      <span
        className={cn(
          showDial ? 'text-input-placeholder' : 'text-transparent'
        )}>
        {dial}&nbsp;
      </span>
      <span
        className={cn(
          showPlaceholder ? 'text-input-placeholder' : 'text-transparent'
        )}>
        {placeholder}
      </span>
    </>
  )
}

export interface PhoneInputProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'onChange'> {
  defaultCountry?: keyof (typeof COUNTRIES)[number]['code']
  onChange: (
    number: string | null,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void
  isError?: boolean
}

// todo: ? rewrite to https://www.npmjs.com/package/react-phone-input-2 ?
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (props: PhoneInputProps, ref) => {
    const { className, defaultCountry, onChange, isError, ...rest } = props
    const [selectedCountry, setSelectedCountry] = useState<Country>(
      () =>
        COUNTRIES.find(country => country.code === (defaultCountry || 'RU'))!
    )
    const [phoneNumber, setPhoneNumber] = useState(
      () => selectedCountry.dialCode
    )
    const [isSelectOpen, setIsSelectOpen] = useState(false)

    const { numberLength, example } = useMemo(() => {
      const exampleNumber = getExampleNumber(selectedCountry.code, examples)!

      return {
        numberLength: exampleNumber?.number.length,
        example: exampleNumber.formatInternational()
      }
    }, [selectedCountry])

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/[^\d+]/g, '')

      if (value === '') {
        setPhoneNumber(`${selectedCountry.dialCode}`)
        return
      }

      const parsedPhoneNumber = parsePhoneNumberFromString(
        value,
        selectedCountry.code
      )

      // check if pasted full local/international number; or number is just valid
      if (parsedPhoneNumber?.isValid()) {
        const country = COUNTRIES.find(
          country => parsedPhoneNumber.country === country.code
        )

        // pasted not supported country
        if (!country) return

        setSelectedCountry(country)
        setPhoneNumber(parsedPhoneNumber.formatInternational())
        onChange(parsedPhoneNumber.number, e)

        return
      }

      // reset outer value if not valid
      onChange(null, e)

      // local number is not valid
      if (!value.startsWith('+')) {
        value = `+${value}`
      }

      // get new country by input value
      if (
        parsedPhoneNumber?.country &&
        parsedPhoneNumber.country !== selectedCountry.code
      ) {
        const country = COUNTRIES.find(
          country => country.code === parsedPhoneNumber.country
        )!

        setSelectedCountry(prev => country || prev)
      }

      // check if pasted/typed number has not enough length
      if (value.length > numberLength) return

      const asYouType = new AsYouType(selectedCountry.code as CountryCode)

      const imcompleteFormattedInput = asYouType.input(value)

      setPhoneNumber(imcompleteFormattedInput)
    }

    return (
      <Popover open={isSelectOpen} onOpenChange={setIsSelectOpen}>
        <PopoverTrigger asChild>
          {/* eslint-disable-next-line */}
          <div
            onClick={e => e.preventDefault()}
            className={cn(
              'w-full flex items-center overflow-hidden rounded-[16px] h-[67px] bg-primary placeholder:text-[16px] placeholder:text-input-placeholder placeholder:leading-[1.1] pl-4',
              className
            )}>
            <div
              className='flex items-center pr-[16px] md:pr-[32px] h-full cursor-pointer'
              role='button'
              tabIndex={0}
              aria-haspopup='listbox'
              aria-expanded={isSelectOpen}
              onClick={() => setIsSelectOpen(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsSelectOpen(true)
                }
              }}>
              <Flag country={selectedCountry} />
            </div>

            <div className='relative flex-1 h-full'>
              <div className='px-1 absolute inset-0 text-accent flex items-center justify-start'>
                <FakePlaceholder phone={phoneNumber} example={example} />
              </div>
              <input
                {...rest}
                // maxLength={inputLength}
                ref={ref}
                type='tel'
                value={phoneNumber}
                placeholder=''
                onChange={handlePhoneChange}
                className={cn(
                  'h-full relative w-full px-1 border-0 focus:outline-none !text-[16px] !leading-[1.1] bg-transparent text-tertiary',
                  isError && 'text-danger'
                )}
              />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent>
          <div
            className='flex flex-col overflow-x-hidden overflow-y-auto max-h-[min(var(--radix-popover-content-available-height),400px)]'
            role='listbox'
            tabIndex={0}
            aria-activedescendant={`${selectedCountry.code}`}>
            {COUNTRIES.map((country, index) => (
              <div
                key={`${index}-${country.code}`}
                id={`${country.code}`}
                role='option'
                aria-selected={country.code === selectedCountry.code}
                tabIndex={0}
                onClick={() => {
                  setSelectedCountry(country)
                  setPhoneNumber(country.dialCode)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedCountry(country)
                    setPhoneNumber(country.dialCode)
                  }
                }}
                className={cn(
                  'flex items-center text-sm rounded-[24px] px-4 py-4 cursor-pointer focus:outline-none gap-2',
                  country.code === selectedCountry.code && 'bg-secondary'
                )}>
                <div className='flex items-center gap-3 justify-between w-full'>
                  <div className='flex items-center gap-3'>
                    <Flag country={country} />
                    <span>{country.name}</span>
                  </div>
                  <span className='text-gray-500 flex-[0_1_auto]'>
                    {country.dialCode}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)
